// routes/AppRoutes.jsx (updated)
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useNetworkStatus } from "../context/NetworkStatusContext";
import { isUniversityManagerDemoPath } from "../utils/universityManagerDemo";

// Pages
import Dashboard from "../pages/Dashboard";
import Universities from "../pages/Universities";
import Campuses from "../pages/Campuses";
import NotFound from "../pages/NotFound";
import Faculties from "../pages/Faculties";
import Departments from "../pages/Departments";
import Clubs from "../pages/Clubs";
import Users from "../pages/Users";
import Categories from "../pages/Categories";
import Events from "../pages/Events";
import Countries from "../pages/Countries";
import Cities from "../pages/Cities";
import NetworkError from "../pages/NetworkError";
import AppConfig from "../pages/AppConfig";

// Components
import ProtectedRoute from "../components/common/ProtectedRoute";
import UniversityManagerRoutes from "./UniversityManagerRoutes";
import UniAdminRoutes from "./UniAdminRoutes";

const AppRoutes = () => {
  const { isServerOnline, isChecking } = useNetworkStatus();
  const location = useLocation();
  const isUmDemoRoute = isUniversityManagerDemoPath(location.pathname);
  const isUniAdminRoute = location.pathname.startsWith("/uni-admin");

  // Show loading while checking server status initially (skip for mock UM demo and uni-admin)
  if (isServerOnline === null && isChecking && !isUmDemoRoute && !isUniAdminRoute) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Network Error Route - Always accessible */}
      <Route path="/network-error" element={<NetworkError />} />

      {/* University Manager mock demo — public, no login or API server required */}
      <Route path="/university-manager/*" element={<UniversityManagerRoutes />} />

      {/* University Admin — real backend, own auth (sessionStorage), accessible without system server */}
      <Route path="/uni-admin/*" element={<UniAdminRoutes />} />

      {/* If server is offline, redirect to network error (except UM demo above) */}
      {isServerOnline === false && (
        <Route path="*" element={<Navigate to="/network-error" replace />} />
      )}

      {/* Normal routes when server is online */}
      {isServerOnline && (
        <>
          {/* Public Routes */}
          {/*
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
            }
          />
          */}
          {/* Redirect old login path to uni-admin login */}
          <Route path="/login" element={<Navigate to="/uni-admin/login" replace />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/universities"
            element={
              <ProtectedRoute>
                <Universities />
              </ProtectedRoute>
            }
          />

          <Route
            path="/campuses"
            element={
              <ProtectedRoute>
                <Campuses />
              </ProtectedRoute>
            }
          />

          <Route
            path="/faculties"
            element={
              <ProtectedRoute>
                <Faculties />
              </ProtectedRoute>
            }
          />

          <Route
            path="/departments"
            element={
              <ProtectedRoute>
                <Departments />
              </ProtectedRoute>
            }
          />

          <Route
            path="/clubs"
            element={
              <ProtectedRoute>
                <Clubs />
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            }
          />

          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <Events />
              </ProtectedRoute>
            }
          />

          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <Categories />
              </ProtectedRoute>
            }
          />

          <Route
            path="/countries"
            element={
              <ProtectedRoute>
                <Countries />
              </ProtectedRoute>
            }
          />

          <Route
            path="/cities"
            element={
              <ProtectedRoute>
                <Cities />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app-config"
            element={
              <ProtectedRoute>
                <AppConfig />
              </ProtectedRoute>
            }
          />

          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/uni-admin/login" replace />} />

          {/* 404 Page */}
          <Route path="*" element={<NotFound />} />
        </>
      )}
    </Routes>
  );
};

export default AppRoutes;
